import { ChangeEventHandler, FC, MouseEvent, ReactNode, useCallback } from 'react';
import { CustomButton, LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Alert, Box, IconButton, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { DatasetFile } from '~/api/types';
import { useDragAndDropFilesUpload } from '~/hooks';
import { FileUploadIcon, FoldersIcon, TrashIcon } from '~/icons';
import { DATE_FORMAT, formatBytes } from '~/utils';
import { fileListItemStyles, fileListStyles, fileUploaderContainerStyles } from './styles';

interface FileUploaderProps {
  existingFiles?: DatasetFile[];
  currentFiles?: File[];
  existingFilesLoading?: boolean;
  onChange?: (files: File[]) => void;
  maxSizeBytes?: number;
  filesToImport?: string;
  supportedDocuments?: string[];
  supportedFormats?: string[];
  buttonLabel?: string;
  secondaryButtonLabel?: string;
  onSecondaryButtonClick?: () => void;
  renderContent?: () => ReactNode;
  showFilesList?: boolean;
  clearFilesAfterUpload?: boolean;
}

const FileUploader: FC<FileUploaderProps> = ({
  existingFiles,
  currentFiles = [],
  existingFilesLoading,
  onChange,
  maxSizeBytes,
  filesToImport = 'documents',
  supportedDocuments,
  supportedFormats,
  buttonLabel = 'Upload Documents',
  secondaryButtonLabel,
  onSecondaryButtonClick,
  renderContent,
  showFilesList = true,
  clearFilesAfterUpload = false,
}) => {
  const { files, setFiles, listeners, error, setError, dragActive, handleValidAppend } = useDragAndDropFilesUpload({
    existingFiles,
    currentFiles,
    supportedFormats,
    maxSizeBytes,
    onChange,
    clearFilesAfterUpload,
  });

  const handleInput = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      if (!e.target.files) return;
      handleValidAppend(Array.from(e.target.files));
      e.target.value = '';
    },
    [handleValidAppend],
  );

  const removeAt = useCallback(
    (idx: number) => {
      const next = files.filter((_, i) => i !== idx);
      setFiles(next);
      onChange?.(next);
    },
    [files, onChange],
  );

  const moreFilesLabel = supportedDocuments?.length ? 'more documents' : 'more data';

  const filesList = [...files, ...(existingFiles || [])].map((file) => ({
    name: file.name,
    lastModified: (file as File)?.lastModified || (file as DatasetFile).date_uploaded,
    size: file.size,
    isDatasetFile: !!(file as DatasetFile).date_uploaded,
  }));

  return (
    <Stack spacing={2} p={3} flex={1}>
      <Paper
        variant="outlined"
        {...listeners}
        sx={fileUploaderContainerStyles(dragActive, !!files.length)}
        role="button"
        onClick={() => document.getElementById('file-input-hidden')?.click()}
      >
        {renderContent?.() || (
          <>
            {files?.length ? (
              <FoldersIcon style={{ width: '32px', height: '32px' }} fill="#1010EB" />
            ) : (
              <FileUploadIcon />
            )}
            <Typography variant={files?.length ? 'h6' : 'h3'}>
              Import {files.length ? moreFilesLabel : filesToImport}
            </Typography>
          </>
        )}
        {!files?.length && (
          <Typography variant="body2" color="text.secondary">
            Drag and drop files here or click to browse
          </Typography>
        )}
        <Stack direction="row" spacing={2}>
          <CustomButton buttonType={files?.length ? 'outlined' : 'primary'}>
            {buttonLabel}
            <input
              id="file-input-hidden"
              type="file"
              hidden
              multiple
              accept={supportedFormats?.join(', ')}
              onChange={handleInput}
            />
          </CustomButton>
          {secondaryButtonLabel && (
            <CustomButton
              buttonType="outlined"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                onSecondaryButtonClick?.();
              }}
            >
              {secondaryButtonLabel}
            </CustomButton>
          )}
        </Stack>
        {!files?.length && (
          <Typography variant="body2">
            {supportedDocuments && `Supported documents: ${supportedDocuments.join(', ')}`}
          </Typography>
        )}
        {!files?.length && supportedFormats && (
          <Typography variant="body2">
            Supported format: {supportedFormats.join(', ')} |{' '}
            {maxSizeBytes && `Max ${formatBytes(maxSizeBytes)} per file`}
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {showFilesList && !!filesList.length && (
        <>
          <Box>
            <Typography variant="h6">{filesToImport}</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2">{filesList.length} Total</Typography>
            </Box>
          </Box>
          <List sx={fileListStyles}>
            {filesList.map(({ name, size, lastModified, isDatasetFile }, i) => (
              <ListItem
                sx={fileListItemStyles}
                key={`${name}-${size}-${lastModified}`}
                secondaryAction={
                  !isDatasetFile && (
                    <IconButton color="error" onClick={() => removeAt(i)}>
                      <TrashIcon />
                    </IconButton>
                  )
                }
              >
                <ListItemText
                  primary={name}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="body2">{formatBytes(size)}</Typography>
                      <Typography variant="body2">|</Typography>
                      <Typography variant="body2" color="textSemanticConfirm.main">
                        Completed
                      </Typography>
                      <Typography variant="body2">|</Typography>
                      <Typography variant="body2">{format(new Date(lastModified), DATE_FORMAT)}</Typography>
                    </Box>
                  }
                  primaryTypographyProps={{ noWrap: true, variant: 'body1' }}
                />
              </ListItem>
            ))}
          </List>
          {existingFilesLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner spinnerSize="m" />
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

export default FileUploader;
